import React, { useState, useEffect } from 'react';
import { Cookie, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';

const COOKIE_KEY = 'zozo_cookie_consent';

export function useCookieConsent() {
  const [consented, setConsented] = useState(() => !!localStorage.getItem(COOKIE_KEY));
  useEffect(() => {
    const handler = () => setConsented(true);
    window.addEventListener('zozo-cookie-accepted', handler);
    return () => window.removeEventListener('zozo-cookie-accepted', handler);
  }, []);
  return consented;
}

export function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_KEY);
    if (!consent) {
      const timer = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(timer);
    }
  }, []);

  const accept = (all) => {
    localStorage.setItem(COOKIE_KEY, JSON.stringify({
      essential: true, analytics: all, marketing: all, timestamp: Date.now()
    }));
    setVisible(false);
    window.dispatchEvent(new Event('zozo-cookie-accepted'));
  };

  if (!visible) return null;

  return (
    <>
      {/* Full-screen backdrop — blocks ALL interactions behind the banner */}
      <div
        className="fixed inset-0 z-[9998] bg-black/60"
        aria-hidden="true"
        data-testid="cookie-backdrop"
      />

      {/* Cookie Banner */}
      <div
        className="fixed bottom-0 left-0 right-0 z-[9999] p-3 sm:p-4 md:p-6"
        data-testid="cookie-banner"
      >
        <div className="max-w-4xl mx-auto bg-neutral-900 border border-neutral-700 rounded-2xl shadow-2xl shadow-black/50 p-4 sm:p-5 md:p-6">
          <div className="flex items-start gap-3 sm:gap-4">
            <div className="p-2 bg-[#A81C1C]/10 rounded-xl flex-shrink-0 hidden sm:flex">
              <Cookie className="w-5 h-5 text-[#A81C1C]" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-white font-semibold text-sm sm:text-base mb-1">Wir verwenden Cookies</h3>
              <p className="text-neutral-400 text-xs sm:text-sm leading-relaxed">
                Wir nutzen Cookies, um dir die bestmögliche Erfahrung auf unserer Website zu bieten.
                Essenzielle Cookies sind für die Funktion der Website notwendig.
                Weitere Informationen findest du in unserer{' '}
                <Link to="/datenschutz" className="text-[#A81C1C] hover:underline">Datenschutzerklärung</Link>.
              </p>

              {showDetails && (
                <div className="mt-3 space-y-2">
                  <div className="flex items-center justify-between p-3 bg-neutral-800 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Shield className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                      <div>
                        <p className="text-white text-xs sm:text-sm font-medium">Essenziell</p>
                        <p className="text-neutral-500 text-[10px] sm:text-xs">Warenkorb, Session, Login</p>
                      </div>
                    </div>
                    <span className="text-[10px] sm:text-xs text-emerald-400 font-medium">Immer aktiv</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-neutral-800 rounded-lg">
                    <div>
                      <p className="text-white text-xs sm:text-sm font-medium">Analyse</p>
                      <p className="text-neutral-500 text-[10px] sm:text-xs">Seitenaufrufe, Performance</p>
                    </div>
                    <span className="text-[10px] sm:text-xs text-neutral-500">Optional</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-neutral-800">
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="text-xs sm:text-sm text-neutral-400 hover:text-white transition-colors px-4 py-2 order-3 sm:order-1"
              data-testid="cookie-details-btn"
            >
              {showDetails ? 'Details ausblenden' : 'Cookie-Details'}
            </button>
            <div className="flex-1" />
            <button
              onClick={() => accept(false)}
              className="px-5 py-3 text-xs sm:text-sm font-medium text-neutral-300 bg-neutral-800 hover:bg-neutral-700 rounded-lg transition-colors order-2 min-h-[44px]"
              data-testid="cookie-reject-btn"
            >
              Nur notwendige
            </button>
            <button
              onClick={() => accept(true)}
              className="px-5 py-3 text-xs sm:text-sm font-medium text-white bg-[#A81C1C] hover:bg-[#8B1616] rounded-lg transition-colors order-1 sm:order-3 min-h-[44px]"
              data-testid="cookie-accept-btn"
            >
              Alle akzeptieren
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
